import { Stack, Typography } from "@mui/material";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { IconDelete, IconEditor } from "@/assets/icons";
import { deleteProductsApi } from "@/api/services/main/organization/product";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { useVisible } from "@/hooks/useVisible";
import { toast } from "@/utils/toast";
import { background, divider, text } from "@/theme/colors";
import { AppRoutes } from "@/routes/config";
import { Button } from "@/modules/irohana-components/elements/Button";
import { FooterActionBar } from "@/modules/irohana-components/elements/FooterActionBar";
import { MenuNavigate } from "@/modules/irohana-components/elements/MenuNavigate";
import { ProductBasicInfoView } from "@/features/organization/product/components/product-detail/ProductBasicInfoView";
import { ProductCombinationView } from "@/features/organization/product/components/product-detail/ProductCombinationView";
import { ProductRefundView } from "@/features/organization/product/components/product-detail/ProductRefundView";
import { ProductTypePriceView } from "@/features/organization/product/components/product-detail/ProductTypePriceView";
import { OrganizationContainer } from "@/features/organization/components/OrganizationContainer";
import {
  PRODUCT_DELETE_ERROR_MESSAGE,
  PRODUCT_DELETE_SUCCESS_MESSAGE,
} from "@/features/organization/product/constants/productEnums";
import { TProductDetail } from "@/api/services/main/organization/product/dtos/product.dto";

type TProductDetailPageProps = {
  product: TProductDetail;
};

export const ProductDetailPage = ({ product }: TProductDetailPageProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const deleteModal = useVisible(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteProductsApi({ ids: [product.id] });
      toast.success(PRODUCT_DELETE_SUCCESS_MESSAGE);
      deleteModal.close();
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PRODUCT.FETCH_LIST] });
      queryClient.removeQueries({ queryKey: [QUERY_KEYS.PRODUCT.FETCH_DETAIL, product.id], exact: true });
      navigate(AppRoutes.organizationProduct);
    } catch {
      toast.error(PRODUCT_DELETE_ERROR_MESSAGE);
    } finally {
      setIsDeleting(false);
    }
  };

  const editUrl = `${AppRoutes.organizationProduct}/${product.id}/edit`;

  return (
    <OrganizationContainer gap={3} pb={4}>
      <Typography variant="head20Bold" color={text.primary}>
        {product.name}
      </Typography>

      <MenuNavigate
        contents={[
          { title: "商品基本情報", content: <ProductBasicInfoView product={product} /> },
          {
            title: "種別単価設定",
            content: (
              <Stack bgcolor={background.white} borderRadius={2} border={`1px solid ${divider.middle}`} overflow="hidden">
                <ProductTypePriceView product={product} />
                <ProductCombinationView product={product} />
              </Stack>
            ),
          },
          { title: "返金条件設定", content: <ProductRefundView product={product} /> },
        ]}
        contentAffix={
          <FooterActionBar>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Button variant="ghost" onClick={() => navigate(AppRoutes.organizationProduct)}>
                戻る
              </Button>
              <Stack direction="row" gap={1.5}>
                {/* 削除 shows only for custom products (固定商品 cannot be deleted). */}
                {!product.isDefault && (
                  <Button variant="danger" isLoading={isDeleting} startIcon={<IconDelete />} onClick={deleteModal.open}>
                    削除
                  </Button>
                )}
                <Button variant="secondary" startIcon={<IconEditor />} onClick={() => navigate(editUrl)}>
                  編集
                </Button>
              </Stack>
            </Stack>
          </FooterActionBar>
        }
      />

      <ConfirmDeleteModal
        open={deleteModal.visible}
        message={`「${product.name}」を削除します。よろしいですか？`}
        onOk={handleDelete}
        onClose={deleteModal.close}
      />
    </OrganizationContainer>
  );
};
